/**
 * Normalizes API questionnaire questions (snake_case, id-based) into the
 * client shape shared by the admin builder and the member-facing dynamic
 * questionnaire flow: camelCase fields and stable string refs, with rules
 * pointing at their destination through destinationRef.
 */

export const questionFromApi = (question) => ({
  ref: String(question.id),
  id: question.id,
  componentType: question.component_type,
  heading: question.heading || '',
  subHeading: question.sub_heading || '',
  body: question.body || '',
  noMentorHeading: question.no_mentor_heading,
  noMentorBody: question.no_mentor_body,
  firstMessageDataKey: question.first_message_data_key || '',
  placeholder: question.placeholder || '',
  dataKey: question.data_key || '',
  dataLabel: question.data_label || '',
  optional: Boolean(question.optional),
  allowOther: Boolean(question.allow_other),
  displayAsStars: Boolean(question.display_as_stars),
  yearOfBirth: Boolean(question.year_of_birth),
  isiKey: question.isi_key || '',
  docusealTemplateId: question.docuseal_template_id ? String(question.docuseal_template_id) : '',
  docusealTemplateName: question.docuseal_template_name || '',
  docusealFieldMappings: (question.docuseal_field_mappings || []).map((mapping) => ({
    field: mapping.field || '',
    dataKey: mapping.data_key || '',
  })),
  options: (question.options || [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((option) => ({
      id: option.id,
      label: option.label,
      value: option.value,
      dataWrites: Object.entries(option.data_writes || {}).map(([key, value]) => ({ key, value: String(value) })),
    })),
  rules: (question.rules || [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((rule) => ({
      id: rule.id,
      operator: rule.operator,
      values: rule.values || [],
      action: rule.action,
      subject: rule.subject || 'answer',
      userAttribute: rule.user_attribute || '',
      answerDataKey: rule.answer_data_key || '',
      destinationRef: rule.destination_question_id ? String(rule.destination_question_id) : '',
      stopperHeading: rule.stopper_heading || '',
      stopperBody: rule.stopper_body || '',
    })),
});

export const questionsFromQuestionnaire = (questionnaire) =>
  ((questionnaire && questionnaire.questions) || [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map(questionFromApi);

// mentor_match is not a traditional question — it runs the live mentor search
// and match card rather than asking anything, so it's excluded from the
// "Question X of Y" progress shown for the rest of the questionnaire.
export const countableQuestions = (questions) =>
  questions.filter((question) => question.componentType !== 'mentor_match');

// Builder chrome (question list, jump-rule picker) needs a title. mentor_match
// stores its optional no-mentor copy in heading, so that field is never the
// question title.
export const questionDisplayTitle = (question) => {
  if (question.componentType === 'mentor_match') return 'Mentor match';
  return question.heading || 'Untitled question';
};

// 1-based position of `question` among countableQuestions, and the total
// countable question count, for progress display. Returns { current: 0, ... }
// for a mentor_match question itself, since it has no progress indicator.
export const progressFor = (question, questions) => {
  const countable = countableQuestions(questions);
  return {
    current: countable.findIndex((candidate) => candidate.ref === question.ref) + 1,
    total: countable.length,
  };
};
